"use client";
/* eslint-disable */

import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import UvegCertificateNft from "../contractsData/UvegCertificateNft.json";

// === Config ===
const CONTRACT_ADDRESS = "0x1a94eAB0488CB975326a0A6080E259441aAeafca";
const ABI = UvegCertificateNft.abi || UvegCertificateNft;
const PREFERRED_GATEWAY = "https://ok-indigo-vole.myfilebase.com/ipfs"; // tu gateway preferido
const POLYGON_RPC = "https://polygon-rpc.com/";

// ===== Helpers =====
function ipfsToPreferred(uri) {
  if (!uri) return "";
  if (!uri.startsWith("ipfs://")) return uri; // ya es http(s)
  const m = uri.match(/^ipfs:\/\/([^/]+)(\/.*)?$/i);
  if (!m) return uri;
  const cid = m[1];
  const rest = m[2] || "";
  return `${PREFERRED_GATEWAY}/${cid}${rest}`;
}

function makeQrUrl(url) {
  if (!url) return "";
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}`;
}

function shortAddr(a) {
  if (!a) return "";
  return a.slice(0, 6) + "…" + a.slice(-4);
}

// ====== Componente principal ======
export default function MyWalletCollections() {
  const [owner, setOwner] = useState(""); // wallet detectada (sin pedir permiso)
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // Lista de NFTs: [{tokenId, imageUrl, tokenUri, qrUrl}]
  const [items, setItems] = useState([]);

  // Buscar otra wallet (manual)
  const [showSearch, setShowSearch] = useState(false);
  const [manualAddr, setManualAddr] = useState("");

  // Detectar cuenta conectada en MetaMask SIN pedir permisos y cargar NFTs
  useEffect(() => {
    let unsub = () => {};
    async function detect() {
      try {
        if (typeof window === "undefined" || !window.ethereum) return;
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accs = await provider.send("eth_accounts", []); // no solicita permisos
        if (accs && accs[0]) {
          setOwner(accs[0]);
          fetchNfts(accs[0]);
        }
        const onAccountsChanged = (accounts) => {
          const a = accounts && accounts[0] ? accounts[0] : "";
          setOwner(a);
          if (a) fetchNfts(a);
          else {
            setItems([]);
          }
        };
        window.ethereum.on?.("accountsChanged", onAccountsChanged);
        unsub = () => window.ethereum.removeListener?.("accountsChanged", onAccountsChanged);
      } catch (_) {}
    }
    detect();
    return () => unsub();
  }, []);

  // === Carga NFTs del propietario ===
  async function fetchNfts(ownerAddr) {
    setMsg("");
    setLoading(true);
    setItems([]);

    try {
      if (!ethers.utils.isAddress(ownerAddr)) {
        setMsg("Dirección inválida");
        return;
      }

      const provider = new ethers.providers.JsonRpcProvider(POLYGON_RPC);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, provider);

      // 1) Obtener token IDs
      let ids = [];
      if (contract.functions?.tokensOfOwner) {
        try {
          // Algunos contratos devuelven BigNumber[]; normalizamos a string[]
          const res = await contract.tokensOfOwner(ownerAddr);
          ids = (res || []).map((bn) => bn.toString());
        } catch (_) {}
      }

      if (!ids || ids.length === 0) {
        const bal = await contract.balanceOf(ownerAddr);
        const b = bal.toNumber ? bal.toNumber() : parseInt(bal);
        for (let i = 0; i < b; i++) {
          const idBn = await contract.tokenOfOwnerByIndex(ownerAddr, i);
          ids.push(idBn.toString());
        }
      }

      if (!ids || ids.length === 0) {
        setMsg("No hay NFTs en esta wallet.");
        return;
      }

      // 2) Para cada tokenId, resolver tokenURI -> imagen
      const promises = ids.map(async (id) => {
        try {
          const tokenUriRaw = await contract.tokenURI(id);
          const tokenUri = ipfsToPreferred(tokenUriRaw);

          // Si es metadata JSON, intentar leer "image"
          let imageUrl = tokenUri;
          if (/\.json(\?|$)/i.test(tokenUri) || tokenUri.endsWith("/") || tokenUri.startsWith(PREFERRED_GATEWAY)) {
            try {
              const res = await fetch(tokenUri, { cache: "no-store" });
              if (res.ok) {
                const meta = await res.json();
                if (meta?.image) imageUrl = ipfsToPreferred(meta.image);
              }
            } catch (_) {}
          }

          return {
            tokenId: id,
            imageUrl,
            tokenUri,
            qrUrl: makeQrUrl(imageUrl),
          };
        } catch (e) {
          return {
            tokenId: id,
            imageUrl: "",
            tokenUri: "",
            qrUrl: "",
            error: e?.message || "No se pudo resolver tokenURI",
          };
        }
      });

      const resolved = await Promise.all(promises);
      // Opcional: ordenar por tokenId asc
      resolved.sort((a, b) => Number(a.tokenId) - Number(b.tokenId));
      setItems(resolved);
    } catch (e) {
      console.error(e);
      setMsg(e?.reason || e?.message || "Error al consultar NFTs");
    } finally {
      setLoading(false);
    }
  }

  async function copy(text) {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setMsg("Copiado al portapapeles");
      setTimeout(() => setMsg(""), 1500);
    } catch (_) {
      setMsg("No se pudo copiar");
      setTimeout(() => setMsg(""), 1500);
    }
  }

  async function downloadPNG(url, tokenId) {
    try {
      if (!url) return;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error("No se pudo descargar el PNG.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nft_${tokenId}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setMsg(e?.message || "No se pudo descargar el PNG");
      setTimeout(() => setMsg(""), 1500);
    }
  }

  async function downloadQR(qrUrl, tokenId) {
    try {
      if (!qrUrl) return;
      const res = await fetch(qrUrl, { cache: "no-store" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `qr_nft_${tokenId}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      setMsg("No se pudo descargar el QR");
      setTimeout(() => setMsg(""), 1500);
    }
  }

  const onImgError = (e) => {
    e.currentTarget.src =
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'>
           <rect width='100%' height='100%' fill='#f1f5f9'/>
           <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
                 font-family='Arial' font-size='16' fill='#64748b'>Vista previa no disponible</text>
         </svg>`
      );
  };

  // ====== Estilos UI ======
  const styles = {
    wrap: { maxWidth: 1200, margin: "0 auto", padding: "16px 18px" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
    title: { margin: 0, fontSize: 20, fontWeight: 800, color: "#0f172a" },
    muted: { color: "#64748b", fontSize: 13 },
    controls: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    btn: { padding: "8px 12px", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" },
    input: { padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 10, minWidth: 280 },

    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 18, marginTop: 12 },
    card: { border: "1px solid #e5e7eb",  overflow: "hidden", background: "#fff", boxShadow: "0 2px 10px rgba(2,8,23,.06)" },
    imgWrap: { padding: 12, background: "#f8fafc" },
    img: { width: "100%", height: "auto", display: "block",  maxHeight: 320, objectFit: "cover" },
    body: { padding: 12 },
    row: { display: "grid", gridTemplateColumns: "110px 1fr", gap: 8, fontSize: 13, marginBottom: 6, color: "#334155" },
    mono: { fontFamily: "monospace", fontSize: 12, color: "#334155", wordBreak: "break-all" },
    titleItem: { margin: 0, fontSize: 15, fontWeight: 800, color: "#0f172a" },
    actions: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10, flexWrap: "wrap" },
    btnPrimary: { padding: "8px 12px", border: "1px solid #2563eb", background: "#2563eb", color: "#fff", cursor: "pointer"},
    badge: { marginTop: 8, fontSize: 12, color: "#0f172a", background: "#eef2ff", border: "1px solid #c7d2fe", padding: "4px 8px",  display: "inline-block" },
    warn: { color: "#b91c1c", marginTop: 8 },
    info: { color: "#0f172a", fontSize: 13, marginTop: 8 },
  };

  return (
    <section style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Mis NFTs Collections</h2>
          <div style={styles.muted}>
            Contrato: <span style={{ fontFamily: "monospace" }}>{CONTRACT_ADDRESS}</span>
          </div>
        </div>

        <div style={styles.controls}>
          <button
            onClick={() => setShowSearch((v) => !v)}
            style={styles.btn}
            title="Consultar otra wallet"
          >
            {showSearch ? "Ocultar búsqueda" : "Buscar otra wallet"}
          </button>
        </div>
      </div>

 

      {/* Buscador de otra wallet */}
      {showSearch && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input
            value={manualAddr}
            onChange={(e) => setManualAddr(e.target.value)}
            placeholder="0x... dirección"
            style={styles.input}
          />
          <button
            onClick={() => fetchNfts((manualAddr || "").trim())}
            disabled={loading}
            style={styles.btn}
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
        </div>
      )}

      {msg && <div style={styles.warn}>{msg}</div>}

      {/* Grid de NFTs */}
      <div style={styles.grid}>
        {loading && items.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} style={styles.card}>
              <div style={styles.imgWrap}>
                <div style={{ height: 220, borderRadius: 12, background: "#e2e8f0" }} />
              </div>
              <div style={styles.body}>
                <div style={{ height: 14, background: "#e2e8f0", borderRadius: 6, marginBottom: 8 }} />
                <div style={{ height: 14, width: "70%", background: "#e2e8f0", borderRadius: 6 }} />
              </div>
            </div>
          ))
        ) : (
          items.map((it) => (
            <div key={`nft-${it.tokenId}`} style={styles.card}>
              <div style={styles.imgWrap}>
                {it.imageUrl ? (
                  <img
                    src={it.imageUrl}
                    alt={`token-${it.tokenId}`}
                    style={styles.img}
                    onError={onImgError}
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div style={{ color: "#6b7280", padding: 12 }}>Imagen no disponible</div>
                )}
              </div>
              <div style={styles.body}>
                <h4 style={styles.titleItem}>NFT · Token ID #{it.tokenId}</h4>
                <div style={styles.row}>
                  <div>Contrato</div>
                  <div style={styles.mono}>{CONTRACT_ADDRESS}</div>
                </div>
                {it.error ? (
                  <div style={{ color: "#b91c1c", fontSize: 12 }}>⚠ {it.error}</div>
                ) : null}

                {/* Acciones */}
                <div style={styles.actions}>
                  <a
                    href={it.imageUrl || "#"}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.btnPrimary}
                    title="Abrir imagen"
                  >
                    Abrir
                  </a>
                  <button
                    onClick={() => copy(it.imageUrl)}
                    style={styles.btn}
                    title="Copiar enlace"
                  >
                    Copiar enlace
                  </button>
                  <button
                    onClick={() => downloadPNG(it.imageUrl, it.tokenId)}
                    style={styles.btn}
                    title="Descargar PNG"
                  >
                    Descargar PNG
                  </button>
                  <button
                    onClick={() => downloadQR(it.qrUrl, it.tokenId)}
                    style={styles.btn}
                    title="Descargar QR"
                  >
                    Descargar QR
                  </button>
                </div>

                {/* Badge con tokenId */}
                <div style={styles.badge}>ID: #{it.tokenId}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
